import project from "../../project.json"
import { supabase } from "./supabase"

/**
 * Is the person signed in an affiliate? — MTC-FCS-037..041.
 * ============================================================================
 * An affiliate is NOT a contractor. They are two different people using two
 * different halves of this product:
 *
 *   a contractor  builds a website, pays for hosting, reads their own leads
 *   an affiliate  makes flyers with QR codes, posts them into Facebook
 *                 groups, and is paid for the sign-ups they bring
 *
 * Until this existed, "affiliate" meant "anybody with an account", and their
 * email was the key their ad codes hung off. So every contractor who logged
 * in could open the marketing dashboard — and, more to the point, an
 * affiliate's numbers were keyed on a string that changes the day somebody
 * updates their email address.
 *
 * Being an affiliate is now a row in its own table, written by the server
 * when somebody is approved. Reading your own row is the whole check.
 */

const TABLE = project.affiliates?.peopleLiveTable || "affiliates"

/**
 * The affiliate record for whoever is signed in, or null.
 *
 * Null for three different situations, and the caller is told which:
 *   no session   nobody is signed in
 *   not listed   signed in, but a contractor rather than a marketer
 *   pending      applied, not approved yet
 */
export async function affiliateFor(user) {
  const client = supabase()
  if (!client || !user?.id) return { status: "anonymous", affiliate: null }

  const { data, error } = await client
    .from(TABLE)
    .select("id,email,name,status,rate_cents")
    .eq("id", user.id)
    .maybeSingle()

  /* An error here is not "not an affiliate" — it is "we could not tell", and
     the two must not look the same. Treating a failed lookup as a refusal
     locks a real affiliate out of their own dashboard and tells them they
     were never approved. */
  if (error) return { status: "unknown", affiliate: null }

  if (!data) return { status: "not-an-affiliate", affiliate: null }
  if (data.status === "pending") return { status: "pending", affiliate: data }
  if (data.status === "paused") return { status: "paused", affiliate: data }

  return { status: "active", affiliate: data }
}

/** What to say for each of the ways this can go, in the person's own terms. */
export const AFFILIATE_MESSAGES = {
  anonymous: {
    title: "Log in to see your adverts",
    body: "This is the marketing side of the site. Log in with the account you applied with.",
  },
  "not-an-affiliate": {
    title: "This account is not set up for adverts",
    body:
      "Affiliate accounts are separate from contractor accounts — this one builds websites rather than " +
      "posting flyers. If you want to promote FreeContractorServices and be paid for sign-ups, apply and " +
      "we will set you up.",
  },
  pending: {
    title: "Your application is being reviewed",
    body:
      "You are on the list. As soon as you are approved you will be able to make adverts here and track " +
      "what they bring in.",
  },
  paused: {
    title: "Your affiliate account is paused",
    body:
      "You cannot make new adverts at the moment. Anything already posted still points at you and is still " +
      "counted — get in touch if this is unexpected.",
  },
  unknown: {
    title: "We could not check your account",
    body: "Something went wrong at our end. Please try again in a moment.",
  },
}
